import React, { useRef, useState } from 'react';

/**
 * One-tap payment-screenshot uploader.
 *
 * A single custom button opens the file picker; selecting a file uploads
 * immediately (no separate "submit" click). On success it shows a
 * pending-approval notice. Shared by the event registration modal and the
 * course payment modal.
 *
 * Props:
 *   noun      — "registration" | "enrollment" (used in the pending message)
 *   upload    — (file) => Promise<{ screenshot: string }>
 *   onUploaded(url) — called once the upload succeeds
 */
export default function ScreenshotUploader({ noun = 'registration', upload, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [error, setError] = useState('');

  const pick = () => { if (inputRef.current) inputRef.current.click(); };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (inputRef.current) inputRef.current.value = ''; // allow re-picking the same file
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image is larger than 5 MB.'); return; }
    setError('');
    setUploading(true);
    try {
      const result = await upload(file);
      setUploadedUrl(result.screenshot);
      if (onUploaded) onUploaded(result.screenshot);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pay-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        hidden
      />

      {uploadedUrl ? (
        <div className="pay-upload__done">
          <p className="pay-upload__done-title">✓ Payment screenshot received.</p>
          <p className="pay-upload__done-msg">
            Your {noun} is pending approval — we'll email you once it's confirmed.
          </p>
          <img src={uploadedUrl} alt="Your payment screenshot" className="pay-upload__thumb" />
          <button type="button" className="pay-upload__relink" onClick={pick} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload a different image'}
          </button>
        </div>
      ) : (
        <>
          {error && <div className="pay-upload__error">{error}</div>}
          <button type="button" className="btn-primary pay-upload__btn" onClick={pick} disabled={uploading}>
            {uploading ? 'Uploading…' : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pay-upload__icon">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload payment screenshot
              </>
            )}
          </button>
          <p className="pay-upload__hint">Required · JPG, PNG or WEBP · max 5 MB</p>
        </>
      )}
    </div>
  );
}
